import { AlertTriangle, ArrowUpRight, ExternalLink, Loader2, UsersRound } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import type { ProjectSummary } from "@/lib/api";
import { getEffectiveStatus, hasFailedDeploy, isAwaitingDeployUrl } from "@/lib/project-status";
import { cn } from "@/lib/utils";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface ProjectCardProps {
  project: ProjectSummary;
  onClick?: () => void;
  /** Resolved name of the project's group, when it's assigned to one and the
   * name is known to the current viewer (see `useMyGroups`/`useGroups`). */
  groupName?: string | null;
}

export function ProjectCard({ project, onClick, groupName }: ProjectCardProps) {
  const status = getEffectiveStatus(project);
  // A project with its own web address only links to it once that address is live.
  const openUrl =
    !project.edge_hostname || project.edge_status === "serving" ? project.app_url : null;

  return (
    <Card
      className={cn(
        "flex flex-col",
        onClick &&
          "focus-visible:ring-ring cursor-pointer transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:outline-none",
      )}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{project.project_name}</CardTitle>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
        {groupName && (
          <Badge variant="outline" className="mt-1 w-fit gap-1 font-normal">
            <UsersRound className="size-3" />
            {groupName}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="flex-1">
        <p className="text-muted-foreground mb-3 line-clamp-2 text-sm break-words">
          {project.description}
        </p>
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          <span>
            {project.owner_name || project.owner_email?.split("@")[0] || "Unknown"} ·{" "}
            {formatDate(project.created_at)}
          </span>
          {project.github_url && (
            <a
              href={project.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              GitHub <ExternalLink className="size-3" />
            </a>
          )}
        </div>
        {project.error_message && (
          <p className="text-destructive mt-2 text-xs">{project.error_message}</p>
        )}
      </CardContent>
      {/* A failed deploy (NOR-456) and a still-working "Open app" link aren't
          mutually exclusive -- a failure keeps the last known-good app_url
          (see DBClient.update_project_app_url), so both can show at once. */}
      {hasFailedDeploy(project) || openUrl ? (
        <CardFooter className="mt-auto flex shrink-0 items-center justify-center gap-4 border-t px-6 py-2.5">
          {hasFailedDeploy(project) &&
            (project.deploy_run_url ? (
              <a
                href={project.deploy_run_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-destructive inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <AlertTriangle className="size-3.5" /> Deploy failed
              </a>
            ) : (
              <span className="text-destructive inline-flex items-center gap-1.5 text-sm font-medium">
                <AlertTriangle className="size-3.5" /> Deploy failed
              </span>
            ))}
          {openUrl && (
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Open app <ArrowUpRight className="size-3.5" />
            </a>
          )}
        </CardFooter>
      ) : isAwaitingDeployUrl(project) ? (
        // Deploy runs after the scaffold completes; the URL arrives via the
        // callback minutes later. Show it's coming (the list auto-refreshes).
        <CardFooter className="mt-auto shrink-0 border-t px-6 py-2.5">
          <span className="text-muted-foreground inline-flex w-full items-center justify-center gap-1.5 text-sm">
            <Loader2 className="size-3.5 animate-spin" /> Deploying app…
          </span>
        </CardFooter>
      ) : null}
    </Card>
  );
}
